import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login({ adminMode = false }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    if (adminMode && r.user.role !== "admin") {
      setError("This account is not an admin.");
      return;
    }
    toast.success("Welcome back!");
    const next = params.get("next");
    navigate(next || (r.user.role === "admin" ? "/admin" : "/"));
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FFF9F2] rangoli-bg">
      <div className="w-full max-w-md bg-white/90 border border-[#FFD1A6] rounded-3xl p-10 shadow-warm">
        <div className="flex items-center gap-3 mb-2">
          <Flame className="w-6 h-6 text-[#E65C00] diya-flame" />
          <span className="font-display text-2xl text-[#2C1810]">Mona&apos;s Creations</span>
        </div>
        <h1 className="font-display text-4xl text-[#2C1810]">{adminMode ? "Admin sign in" : "Welcome back"}</h1>
        <p className="font-body text-sm text-[#5B3D2A] mt-2">Sign in to continue your festive journey.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input data-testid="login-email" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-white border-[#FFD1A6]" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input data-testid="login-password" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-white border-[#FFD1A6]" />
          </div>
          {error && <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>}
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white h-12 btn-lift"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        {!adminMode && (
          <p className="text-sm font-body text-[#5B3D2A] mt-6 text-center">
            New to Mona&apos;s Creations?{" "}
            <Link data-testid="login-to-register" to="/register" className="text-[#4A148C] font-semibold hover:text-[#E65C00]">Create an account</Link>
          </p>
        )}
      </div>
    </div>
  );
}
